// Thin fetch wrappers for the share-link public edge functions.
//
// Both endpoints are unauthenticated — the token in the URL is the auth
// credential. We pass the project's anon `apikey` only so the Supabase
// gateway accepts the call.

import type { TutorialCardV2 } from '@little-loop/shared-types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

function functionsBase(): string {
  if (!SUPABASE_URL) {
    throw new Error('VITE_SUPABASE_URL is not set.');
  }
  return `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1`;
}

function headers(): HeadersInit {
  if (!SUPABASE_ANON_KEY) {
    throw new Error('VITE_SUPABASE_ANON_KEY is not set.');
  }
  return {
    apikey: SUPABASE_ANON_KEY,
    'Content-Type': 'application/json',
  };
}

// ── Lookup ────────────────────────────────────────────────────────────────

export type LookupErrorCode =
  | 'invalid_format'
  | 'rate_limited'
  | 'not_found'
  | 'revoked'
  | 'expired'
  | 'unsupported_scope'
  | 'tutorial_not_found'
  | 'tutorial_unavailable';

export interface LookupTutorial {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  topic_code: string | null;
  cards: TutorialCardV2[];
  variables: VariableDefinition[];
  /**
   * Author-configured outcomes for this template. The player keys off
   * `outcome_code` on terminal cards to render the matching emoji /
   * label / display_message / color. Empty when the template predates
   * the outcomes data model — the player falls back to the legacy
   * success/failure rendering.
   */
  outcomes?: LookupOutcome[];
  /** Optional per-template theme override (color_primary, text_dir, …). */
  theme?: Record<string, unknown> | null;
}

export interface LookupOutcome {
  code: string;
  label: string;
  kind: 'success' | 'failure' | 'neutral';
  emoji: string | null;
  display_message: string | null;
  color_hex: string | null;
  sort_order?: number;
}

export interface VariableDefinition {
  name: string;
  display_name?: string;
  example_value?: string | null;
}

export type LookupResult =
  | {
      valid: true;
      recipient_first_name: string;
      manager_name: string;
      expires_at: string;
      already_completed: boolean;
      tutorial: LookupTutorial;
    }
  | {
      valid: false;
      error: LookupErrorCode;
      expires_at?: string;
    };

interface SupabaseEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export async function lookupShareLink(token: string): Promise<LookupResult> {
  const res = await fetch(
    `${functionsBase()}/share-link-lookup?token=${encodeURIComponent(token)}`,
    { method: 'GET', headers: headers() },
  );
  const body = (await res.json()) as SupabaseEnvelope<LookupResult>;
  if (!res.ok || !body.success || !body.data) {
    throw new Error(body.error?.message ?? `Lookup failed (${res.status})`);
  }
  return body.data;
}

// ── Complete ──────────────────────────────────────────────────────────────

export interface CompleteRequest {
  token: string;
  completion_time_seconds?: number;
  branch_path?: string[];
}

export interface CompleteResult {
  ok?: boolean;
  already_completed?: boolean;
  completed_at?: string;
  notified?: boolean;
}

export async function completeShareLink(req: CompleteRequest): Promise<CompleteResult> {
  const res = await fetch(`${functionsBase()}/share-link-complete`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(req),
  });
  const body = (await res.json()) as SupabaseEnvelope<CompleteResult>;
  if (!res.ok || !body.success) {
    throw new Error(body.error?.message ?? `Completion failed (${res.status})`);
  }
  return body.data ?? {};
}
